import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Bell, MapPin, Stethoscope, HeartPulse, Home, Video, AlertTriangle, FlaskConical, Pill, Users, ChevronRight, Star, Clock, ShieldCheck } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { professionals, specialties } from '../data/mockData';
export function HomeScreen() {
  const navigate = useNavigate();
  const {
    user
  } = useAppContext();
  const quickActions = [{
    id: 'doctor',
    title: 'Book Doctor',
    icon: Stethoscope,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    id: 'nurse',
    title: 'Find Nurse',
    icon: HeartPulse,
    color: 'text-pink-600',
    bg: 'bg-pink-100'
  }, {
    id: 'home',
    title: 'Home Visit',
    icon: Home,
    color: 'text-purple-600',
    bg: 'bg-purple-100'
  }, {
    id: 'video',
    title: 'Video Consult',
    icon: Video,
    color: 'text-indigo-600',
    bg: 'bg-indigo-100'
  }, {
    id: 'emergency',
    title: 'Emergency',
    icon: AlertTriangle,
    color: 'text-white',
    bg: 'bg-status-emergency'
  }, {
    id: 'lab',
    title: 'Lab Tests',
    icon: FlaskConical,
    color: 'text-teal-600',
    bg: 'bg-teal-100'
  }, {
    id: 'meds',
    title: 'Refill Meds',
    icon: Pill,
    color: 'text-orange-600',
    bg: 'bg-orange-100'
  }, {
    id: 'family',
    title: 'Family Care',
    icon: Users,
    color: 'text-green-600',
    bg: 'bg-green-100'
  }];
  return <div className="min-h-screen bg-ui-card pb-6" data-id="element-891">
      {/* Header */}
      <div className="bg-brand-primary pt-12 pb-6 px-6 rounded-b-[32px] shadow-sm relative z-10" data-id="element-892">
        <div className="flex justify-between items-center mb-6" data-id="element-893">
          <div data-id="element-894">
            <p className="text-brand-light text-sm font-medium mb-1" data-id="element-895">
              Good morning,
            </p>
            <h1 className="text-white text-2xl font-bold" data-id="element-896">
              {user?.name || 'Guest'}
            </h1>
          </div>
          <div className="flex items-center space-x-3" data-id="element-897">
            <div className="flex items-center bg-white/20 px-3 py-1.5 rounded-full backdrop-blur-sm" data-id="element-898">
              <MapPin size={14} className="text-white mr-1" data-id="element-899" />
              <span className="text-white text-xs font-medium truncate max-w-[80px]" data-id="element-900">
                Lekki Phase 1
              </span>
            </div>
            <button onClick={() => navigate('/notifications')} className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm relative" data-id="element-901">
              <Bell size={20} className="text-white" data-id="element-902" />
              <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-status-emergency rounded-full border-2 border-brand-primary" data-id="element-903"></span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div onClick={() => navigate('/explore')} className="bg-white rounded-2xl p-4 flex items-center shadow-md active:scale-[0.98] transition-transform cursor-text" data-id="element-904">
          <Search size={20} className="text-text-secondary mr-3" data-id="element-905" />
          <span className="text-text-secondary text-sm" data-id="element-906">
            Search symptoms, doctors, specialties...
          </span>
        </div>
      </div>

      <div className="px-6 mt-6 space-y-8" data-id="element-907">
        {/* Quick Actions Grid */}
        <div className="grid grid-cols-4 gap-y-6 gap-x-2" data-id="element-908">
          {quickActions.map(action => <button key={action.id} onClick={() => {
          switch (action.id) {
            case 'doctor':
              navigate('/flow/book-doctor');
              break;
            case 'nurse':
              navigate('/flow/find-nurse');
              break;
            case 'home':
              navigate('/flow/home-visit');
              break;
            case 'video':
              navigate('/flow/video-consult');
              break;
            case 'emergency':
              navigate('/emergency');
              break;
            case 'lab':
              navigate('/flow/lab-tests');
              break;
            case 'meds':
              navigate('/flow/refill-meds');
              break;
            case 'family':
              navigate('/flow/family-care');
              break;
            default:
              navigate('/explore');
          }
        }} className="flex flex-col items-center" data-id="element-909">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-2 shadow-sm ${action.bg}`} data-id="element-910">
                <action.icon size={24} className={action.color} data-id="element-911" />
              </div>
              <span className="text-[11px] font-semibold text-text-main text-center leading-tight" data-id="element-912">
                {action.title}
              </span>
            </button>)}
        </div>

        {/* Symptom Checker Card */}
        <motion.div whileTap={{
        scale: 0.98
      }} onClick={() => navigate('/symptom-checker')} className="bg-gradient-to-r from-brand-dark to-brand-primary rounded-2xl p-5 text-white shadow-md relative overflow-hidden flex items-center justify-between" data-id="element-913">
          <div className="absolute right-0 top-0 opacity-10 transform translate-x-4 -translate-y-4" data-id="element-914">
            <Stethoscope size={100} data-id="element-915" />
          </div>
          <div className="relative z-10" data-id="element-916">
            <h3 className="font-bold text-lg mb-1" data-id="element-917">Not sure who to book?</h3>
            <p className="text-brand-light text-sm mb-3" data-id="element-918">
              Check your symptoms and get guided.
            </p>
            <button className="bg-white text-brand-primary text-sm font-semibold px-4 py-2 rounded-lg shadow-sm" data-id="element-919">
              Start symptom check
            </button>
          </div>
        </motion.div>

        {/* Nearby Professionals */}
        <div data-id="element-920">
          <div className="flex justify-between items-end mb-4" data-id="element-921">
            <h2 className="text-lg font-bold text-text-main" data-id="element-922">
              Nearby Verified Professionals
            </h2>
            <button onClick={() => navigate('/explore')} className="text-brand-primary text-sm font-medium flex items-center" data-id="element-923">
              See all <ChevronRight size={16} data-id="element-924" />
            </button>
          </div>

          <div className="flex overflow-x-auto no-scrollbar -mx-6 px-6 space-x-4 pb-4" data-id="element-925">
            {professionals.slice(0, 4).map(prof => <div key={prof.id} onClick={() => navigate(`/profile/${prof.id}`)} className="min-w-[240px] bg-white rounded-2xl p-4 shadow-card border border-ui-border active:scale-[0.98] transition-transform" data-id="element-926">
                <div className="flex items-start space-x-3 mb-3" data-id="element-927">
                  <img src={prof.image} alt={prof.name} className="w-14 h-14 rounded-xl object-cover" data-id="element-928" />
                  <div data-id="element-929">
                    <div className="flex items-center" data-id="element-930">
                      <h3 className="font-bold text-text-main text-sm line-clamp-1" data-id="element-931">
                        {prof.name}
                      </h3>
                      {prof.verified && <ShieldCheck size={14} className="text-brand-primary ml-1 shrink-0" data-id="element-932" />}
                    </div>
                    <p className="text-xs text-text-secondary" data-id="element-933">
                      {prof.specialty}
                    </p>
                    <div className="flex items-center mt-1" data-id="element-934">
                      <Star size={12} className="text-status-warning fill-current" data-id="element-935" />
                      <span className="text-xs font-medium ml-1" data-id="element-936">
                        {prof.rating}
                      </span>
                      <span className="text-xs text-text-secondary ml-1" data-id="element-937">
                        ({prof.reviewsCount})
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center text-xs text-text-secondary mb-3 bg-ui-card p-2 rounded-lg" data-id="element-938">
                  <Clock size={14} className="mr-1.5 text-brand-primary" data-id="element-939" />
                  <span className="truncate" data-id="element-940">{prof.nextAvailable}</span>
                </div>

                <div className="flex items-center justify-between mt-2" data-id="element-941">
                  <div className="font-bold text-text-main" data-id="element-942">
                    ₦{prof.fee.toLocaleString()}
                  </div>
                  <button className="bg-brand-light text-brand-primary text-xs font-semibold px-3 py-1.5 rounded-lg" data-id="element-943">
                    Book Now
                  </button>
                </div>
              </div>)}
          </div>
        </div>

        {/* Popular Specialties */}
        <div data-id="element-944">
          <h2 className="text-lg font-bold text-text-main mb-4" data-id="element-945">
            Popular Specialties
          </h2>
          <div className="flex flex-wrap gap-2" data-id="element-946">
            {specialties.slice(0, 6).map((spec, idx) => <button key={idx} className="bg-white border border-ui-border px-4 py-2 rounded-full text-sm font-medium text-text-main shadow-sm active:bg-ui-card" data-id="element-947">
                {spec}
              </button>)}
          </div>
        </div>
      </div>
    </div>;
}